import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { BRAND, NOW_EXPORTING, IMAGES } from "@/data/site";
import { LogoMark } from "@/components/LogoMark";
import { useI18n } from "@/context/I18nContext";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } } };
const lineMask = { hidden: { y: "110%" }, show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } } };

export default function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const lines = [t("hero_l1"), t("hero_l2"), t("hero_l3")];

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-brand-gold/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-brand-goldSoft blur-3xl opacity-60" />

      <div className="relative z-10 mx-auto max-w-7xl w-full px-6 lg:px-10 pt-32 pb-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-screen">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-brand-gold mb-7"
          >
            <span className="h-px w-10 bg-brand-gold/70" />
            {t("hero_badge")}
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-semibold text-brand-fg tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-0.5">
                <motion.span variants={lineMask} className={`block ${i === 2 ? "text-brand-gold italic" : ""}`}>
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-7 max-w-xl text-brand-fg/70 text-lg sm:text-xl leading-relaxed"
          >
            {t("hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href="#products" data-testid="hero-explore-btn" className="rounded-full bg-brand-gold px-8 py-3.5 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors">
              {t("hero_explore")}
            </a>
            <a href={`https://wa.me/${BRAND.whatsappRaw}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="flex items-center gap-2 rounded-full border border-brand-fg/20 px-7 py-3.5 text-sm font-semibold text-brand-fg hover:border-brand-gold hover:text-brand-gold transition-colors">
              <MessageCircle size={16} /> {t("hero_whatsapp")}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-brand-border shadow-xl aspect-[4/5]">
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Premium Indian export products"
              className="absolute inset-0 h-[118%] w-full object-cover"
            />
            <LogoMark size="h-24 w-24" className="absolute top-5 left-5 z-10" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            className="absolute -bottom-6 -left-6 rounded-2xl border border-brand-border bg-brand-card/90 backdrop-blur px-6 py-4 shadow-lg"
          >
            <div className="font-display text-3xl text-brand-gold">10+</div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-muted">Product lines</div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#products"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-brand-muted"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.a>
    </section>
  );
}
